/**
 * Project: Aniatek
 * Author: Carlos A. Loaiza | Connect & Launch
 * Tel: +52 (667) 247 - 6316
 * Intellectual Property: All rights reserved (c) 2026
 */

<template>
  <LayoutPrincipal>
    <ContainerWhite>
      <div class="membership-wall-container">
        <div class="membership-card">
          <div class="icon-section">
            <i class="fa-solid fa-lock wall-icon"></i>
          </div>

          <h1 class="wall-title">¡Estás a un paso de comenzar!</h1>

          <p class="wall-subtitle">
            Tu registro ha sido exitoso, pero para acceder a la plataforma completa,
            generar cotizaciones, y gestionar prospectos, necesitas activar tu membresía.
          </p>

          <div class="benefit-list">
            <h3>Beneficios Exclusivos:</h3>
            <ul>
              <li><i class="fa-solid fa-circle-check check-icon"></i> Generación ilimitada de cotizaciones.</li>
              <li><i class="fa-solid fa-circle-check check-icon"></i> Acceso al módulo de administración de prospectos.</li>
              <li><i class="fa-solid fa-circle-check check-icon"></i> Soporte prioritario 24/7.</li>
            </ul>
          </div>

          <p v-if="errorMsg" class="error-text">{{ errorMsg }}</p>

          <button 
            @click="startMembershipRedirect" 
            class="cta-button" 
            :disabled="cargando || cargandoConfig"
          >
            <template v-if="cargando">
              <i class="fas fa-spinner fa-spin"></i> Conectando con Stripe...
            </template>
            <template v-else>
              <span v-if="configPagos.trial_activo">
                ¡Comienza tu mes gratis ahora!
              </span>
              <span v-else>
                Empieza hoy por ${{ configPagos.precio }}/mes
              </span>
            </template>
          </button>

          <p v-if="configPagos.trial_activo" class="legal-note">
            * Se requiere tarjeta para activar la prueba de {{ configPagos.trial_dias }} días. 
            No se te cobrará nada hoy. Puedes cancelar en cualquier momento.
          </p>

          <p class="wall-footer">
            ¿Ya pagaste? Cierra sesión y vuelve a entrar o contacta a soporte.
          </p>
          
          <p class="secure-note">
            <i class="fas fa-lock"></i> Pago 100% seguro vía Stripe
          </p>
        </div>
      </div>
    </ContainerWhite>
  </LayoutPrincipal>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import LayoutPrincipal from '@/layouts/layoutPrincipal.vue';
import ContainerWhite from '@/layouts/ContainerWhite.vue';
import pagosService from '@/services/pagosService';

const cargando = ref(false);
const cargandoConfig = ref(true);
const errorMsg = ref('');

const configPagos = ref({
  trial_activo: false,
  trial_dias: 30,
  precio: 999
});

onMounted(async () => {
  try {
    const response = await pagosService.obtenerConfiguracion();
    if (response.data) {
      configPagos.value = response.data;
    }
  } catch (error) {
    console.error("Error al obtener configuración de pagos:", error);
  } finally {
    cargandoConfig.value = false;
  }
});

const startMembershipRedirect = async () => {
  cargando.value = true;
  errorMsg.value = '';
  
  try {
    const response = await pagosService.crearSesionDePago();
    
    if (response.data && response.data.url) {
      window.location.href = response.data.url;
    } else {
      errorMsg.value = "No se pudo obtener el enlace de pago. Intenta más tarde.";
    }
  } catch (error) {
    console.error("Error al redirigir a Stripe:", error);
    if (error.response && error.response.status === 401) {
      errorMsg.value = "Tu sesión ha expirado. Por favor, vuelve a iniciar sesión.";
    } else {
      errorMsg.value = "Hubo un problema al conectar con el servidor de pagos.";
    }
  } finally {
    cargando.value = false;
  }
};
</script>

<style scoped>
.membership-wall-container {
  --primary-color: #29b9f6;
  --secondary-color: #f7f3f0;
  --text-color: #333;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 76vh;
}

.membership-card {
  background: #ffffff;
  padding: 40px;
  border-radius: 15px;
  text-align: center;
  max-width: 550px;
  width: 100%;
}

.icon-section {
  margin-bottom: 25px;
}

.wall-icon {
  font-size: 50px;
  color: var(--primary-color);
  background-color: rgba(152, 77, 33, 0.1);
  padding: 20px;
  border-radius: 50%;
}

.wall-title {
  font-size: 30px;
  color: var(--primary-color);
  margin-bottom: 15px;
  font-weight: 700;
}

.wall-subtitle {
  font-size: 18px;
  color: var(--text-color);
  margin-bottom: 30px;
  line-height: 1.5;
  text-align: justify;
}

.benefit-list {
  text-align: left;
  margin: 30px auto;
  padding: 0 20px;
  border-left: 4px solid var(--primary-color);
}

.benefit-list h3 {
  color: var(--primary-color);
  margin-bottom: 10px;
}

.benefit-list ul {
  list-style: none;
  padding: 0;
}

.benefit-list li {
  margin-bottom: 8px;
  font-size: 16px;
  color: #555;
}

.check-icon {
  color: #28a745;
  margin-right: 8px;
}

.cta-button {
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 15px 30px;
  font-size: 20px;
  font-weight: bold;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  margin-top: 20px;
  box-shadow: 0 4px 10px rgba(152, 77, 33, 0.4);
}

.cta-button:hover:not(:disabled) {
  background-color: #a8623d;
  transform: translateY(-2px);
}

.cta-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
  box-shadow: none;
}

.legal-note {
  font-size: 12px;
  color: #666;
  margin-top: 10px;
  line-height: 1.4;
}

.error-text {
  color: #d93025;
  background-color: #f8d7da;
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 15px;
  font-size: 14px;
}

.wall-footer {
  margin-top: 20px;
  font-size: 14px;
  color: #999;
}

.secure-note {
  margin-top: 15px;
  font-size: 12px;
  color: #635bff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-weight: 600;
}

@media (max-width: 600px) {
  .membership-card {
    padding: 25px 20px;
    margin: 20px;
  }
  .wall-title { font-size: 24px; }
  .wall-subtitle { font-size: 16px; }
  .cta-button { font-size: 18px; }
}
</style>